import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import {
  Gift, Trophy, Star, Coins, CreditCard, Sparkles, Clock,
  Users, ChevronRight, Loader2, Check, Award, Zap, Crown, Target, MessageCircle
} from "lucide-react";
import { formatDistanceToNow, format } from "date-fns";
import AIPalChat from "@/components/AIPalChat";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RewardsCentre() {
  const [activeTab, setActiveTab] = useState("rewards");
  const [myRewards, setMyRewards] = useState({ rewards: [], total_earned: 0, current_balance: 0 });
  const [competitions, setCompetitions] = useState([]);
  const [offers, setOffers] = useState([]);
  const [creditPackages, setCreditPackages] = useState([]);
  const [transactionHistory, setTransactionHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCompetition, setSelectedCompetition] = useState(null);
  const [showCreateCompetition, setShowCreateCompetition] = useState(false);
  const [showAIPal, setShowAIPal] = useState(false);
  const [currencyInfo, setCurrencyInfo] = useState({ symbol: "$", code: "USD" });
  const [aippsValue, setAippsValue] = useState(null);

  // Create competition form
  const [compForm, setCompForm] = useState({
    title: "",
    description: "",
    entry_cost: 0,
    prize_pool: 0,
    prize_description: "",
    max_participants: null,
    ends_at: ""
  });

  const getHeaders = () => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchMyRewards = async () => {
    try {
      const res = await axios.get(`${API}/rewards/my-rewards`, { headers: getHeaders() });
      setMyRewards(res.data);
    } catch (err) {
      console.error("Failed to fetch rewards", err);
    }
  };

  const fetchCompetitions = async () => {
    try {
      const res = await axios.get(`${API}/rewards/competitions`, { headers: getHeaders() });
      setCompetitions(res.data || []);
    } catch (err) {
      console.error("Failed to fetch competitions", err);
    }
  };

  const fetchOffers = async () => {
    try {
      const res = await axios.get(`${API}/rewards/offers`, { headers: getHeaders() });
      setOffers(res.data || []);
    } catch (err) {
      console.error("Failed to fetch offers", err);
    }
  };

  const fetchCreditPackages = async () => {
    try {
      // Fetch localized packages with currency conversion
      const res = await axios.get(`${API}/currency/packages`, { headers: getHeaders() });
      setCreditPackages(res.data.packages || []);
      if (res.data.currency) {
        setCurrencyInfo({ code: res.data.currency, symbol: res.data.packages?.[0]?.local_symbol || "$" });
      }
    } catch (err) {
      console.error("Failed to fetch packages", err);
      // Fallback to regular packages
      try {
        const fallback = await axios.get(`${API}/rewards/credit-packages`, { headers: getHeaders() });
        setCreditPackages(fallback.data.packages || []);
      } catch {}
    }
  };

  const fetchAippsValue = async (balance) => {
    try {
      const res = await axios.get(`${API}/currency/aipps-value?aipps=${balance}`, { headers: getHeaders() });
      setAippsValue(res.data);
    } catch (err) {
      console.error("Failed to fetch aipps value", err);
    }
  };

  const fetchHistory = async () => {
    try {
      const res = await axios.get(`${API}/rewards/history`, { headers: getHeaders() });
      setTransactionHistory(res.data.transactions || []);
    } catch (err) {
      console.error("Failed to fetch history", err);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchMyRewards(),
        fetchCompetitions(),
        fetchOffers(),
        fetchCreditPackages(),
        fetchHistory()
      ]);
      setLoading(false);
    };
    loadData();
  }, []);

  // Fetch Aipps value when balance changes
  useEffect(() => {
    if (myRewards.current_balance > 0) {
      fetchAippsValue(myRewards.current_balance);
    }
  }, [myRewards.current_balance]);

  const handleEnterCompetition = async (compId) => {
    try {
      await axios.post(`${API}/rewards/competitions/${compId}/enter`, {}, {
        headers: { ...getHeaders(), "Content-Type": "application/json" }
      });
      toast.success("Successfully entered competition!");
      fetchCompetitions();
      fetchMyRewards();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to enter competition");
    }
  };

  const handleClaimOffer = async (offerId) => {
    try {
      const res = await axios.post(`${API}/rewards/offers/${offerId}/claim`, {}, {
        headers: { ...getHeaders(), "Content-Type": "application/json" }
      });
      toast.success(`Claimed ${res.data.reward} AI!`);
      fetchOffers();
      fetchMyRewards();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to claim offer");
    }
  };

  const handleCreateCompetition = async () => {
    if (!compForm.title || !compForm.description || !compForm.prize_description || !compForm.ends_at) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      await axios.post(`${API}/rewards/competitions/create`, compForm, {
        headers: { ...getHeaders(), "Content-Type": "application/json" }
      });
      toast.success("Competition created!");
      setShowCreateCompetition(false);
      setCompForm({ title: "", description: "", entry_cost: 0, prize_pool: 0, prize_description: "", max_participants: null, ends_at: "" });
      fetchCompetitions();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create competition");
    }
  };

  const handlePurchaseCredits = async (packageId) => {
    try {
      // Use the correct credits checkout endpoint
      const res = await axios.post(`${API}/credits/checkout`, 
        { 
          package_id: packageId,
          origin_url: window.location.origin
        },
        { headers: { ...getHeaders(), "Content-Type": "application/json" } }
      );
      
      if (res.data.url) {
        toast.success("Redirecting to checkout...");
        window.location.href = res.data.url;
      } else {
        toast.error("Failed to create checkout session");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Purchase failed");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <div className="bg-gradient-to-r from-amber-500 to-orange-500 p-6 text-white">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-['Barlow_Condensed'] text-3xl font-bold flex items-center gap-3">
                <Gift className="w-8 h-8" />
                REWARDS CENTRE
              </h1>
              <p className="text-amber-100 mt-1">Earn rewards, compete, and grow your AI</p>
            </div>
            <div className="flex items-center gap-4">
              {/* RewardsPal Button */}
              <Button
                onClick={() => setShowAIPal(!showAIPal)}
                variant="outline"
                className="border-white hover:bg-white/20"
                data-testid="rewardspal-btn"
              >
                <MessageCircle className="w-4 h-4 mr-2" />
                RewardsPal
              </Button>
              <Card className="bg-white/20 backdrop-blur border-white/30">
                <CardContent className="p-4 flex items-center gap-3">
                  <Coins className="w-8 h-8 text-amber-200" />
                  <div>
                    <p className="text-sm text-amber-100">Your Balance</p>
                    <p className="text-2xl font-bold">{myRewards.current_balance} {myRewards.current_balance === 1 ? "AI" : "AIs"}</p>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-white/20 backdrop-blur border-white/30">
                <CardContent className="p-4 flex items-center gap-3">
                  <Star className="w-8 h-8 text-amber-200" />
                  <div>
                    <p className="text-sm text-amber-100">Total Earned</p>
                    <p className="text-2xl font-bold">{myRewards.total_earned} {myRewards.total_earned === 1 ? "AI" : "AIs"}</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="rewards" className="gap-2">
              <Award className="w-4 h-4" />
              My Rewards
            </TabsTrigger>
            <TabsTrigger value="competitions" className="gap-2">
              <Trophy className="w-4 h-4" />
              Competitions
            </TabsTrigger>
            <TabsTrigger value="offers" className="gap-2">
              <Sparkles className="w-4 h-4" />
              Offers
            </TabsTrigger>
              <TabsTrigger value="purchase" className="gap-2">
                <CreditCard className="w-4 h-4" />
                Purchase
              </TabsTrigger>
            <TabsTrigger value="history" className="gap-2">
              <Clock className="w-4 h-4" />
              History
            </TabsTrigger>
          </TabsList>

          {/* My Rewards Tab */}
          <TabsContent value="rewards">
            <div className="grid grid-cols-3 gap-6">
              {/* Stats Cards */}
              <Card className="bg-gradient-to-br from-amber-500 to-orange-500 text-white">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center">
                      <Coins className="w-7 h-7" />
                    </div>
                    <div>
                      <p className="text-amber-100">Current Balance</p>
                      <p className="text-3xl font-bold">{myRewards.current_balance}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-emerald-500 to-teal-500 text-white">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center">
                      <Star className="w-7 h-7" />
                    </div>
                    <div>
                      <p className="text-emerald-100">Total Earned</p>
                      <p className="text-3xl font-bold">{myRewards.total_earned}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-purple-500 to-indigo-500 text-white">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center">
                      <Trophy className="w-7 h-7" />
                    </div>
                    <div>
                      <p className="text-purple-100">Rewards Earned</p>
                      <p className="text-3xl font-bold">{myRewards.rewards.length}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Rewards List */}
            <Card className="mt-6">
              <CardHeader>
                <CardTitle>Reward History</CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[400px]">
                  {myRewards.rewards.length === 0 ? (
                    <div className="text-center py-12">
                      <Gift className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                      <p className="text-slate-500">No rewards yet</p>
                      <p className="text-sm text-slate-400 mt-1">Participate in competitions and claim offers to earn rewards!</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {myRewards.rewards.map((reward, i) => (
                        <div key={i} className="flex items-center justify-between p-4 bg-app rounded-lg">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                              <Award className="w-5 h-5 text-amber-600" />
                            </div>
                            <div>
                              <p className="font-medium text-slate-800">{reward.source}</p>
                              <p className="text-sm text-slate-500">{reward.type}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-amber-600">+{reward.amount} {reward.amount === 1 ? "AI" : "AIs"}</p>
                            <p className="text-xs text-slate-500">
                              {formatDistanceToNow(new Date(reward.earned_at), { addSuffix: true })}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Competitions Tab */}
          <TabsContent value="competitions">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold text-slate-800">Active Competitions</h2>
              <Button onClick={() => setShowCreateCompetition(true)} data-testid="create-competition-btn">
                <Trophy className="w-4 h-4 mr-2" />
                Create Competition
              </Button>
            </div>

            {competitions.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Trophy className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No active competitions</p>
                  <p className="text-sm text-slate-400 mt-1">Be the first to create one!</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-2 gap-6">
                {competitions.map(comp => (
                  <Card key={comp.id} className="hover:shadow-lg transition" data-testid={`competition-${comp.id}`}>
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div>
                          <CardTitle className="text-lg">{comp.title}</CardTitle>
                          <CardDescription className="mt-1">{comp.description}</CardDescription>
                        </div>
                        <Badge className={comp.status === "active" ? "bg-green-500" : "bg-app0"}>
                          {comp.status}
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div className="flex items-center gap-2">
                          <Coins className="w-4 h-4 text-amber-500" />
                      <span>Entry: {comp.entry_cost || "Free"} {comp.entry_cost === 1 ? "AI" : "AIs"}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Trophy className="w-4 h-4 text-amber-500" />
                      <span>Prize: {comp.prize_pool} {comp.prize_pool === 1 ? "AI" : "AIs"}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Users className="w-4 h-4 text-slate-500" />
                          <span>{comp.participants_count || 0} participants</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4 text-slate-500" />
                          <span>Ends {formatDistanceToNow(new Date(comp.ends_at), { addSuffix: true })}</span>
                        </div>
                      </div>
                      <p className="mt-4 text-sm text-slate-600">
                        <strong>Prize:</strong> {comp.prize_description}
                      </p>
                    </CardContent>
                    <CardFooter>
                      {comp.user_entered ? (
                        <Button disabled className="w-full" variant="outline">
                          <Check className="w-4 h-4 mr-2" />
                          Already Entered
                        </Button>
                      ) : (
                        <Button
                          onClick={() => handleEnterCompetition(comp.id)}
                          className="w-full bg-amber-500 hover:bg-amber-600"
                          data-testid={`enter-competition-${comp.id}`}
                        >
                          Enter Competition
                          {comp.entry_cost > 0 && ` (${comp.entry_cost} ${comp.entry_cost === 1 ? "AI" : "AIs"})`}
                        </Button>
                      )}
                    </CardFooter>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Offers Tab */}
          <TabsContent value="offers">
            <h2 className="text-xl font-semibold text-slate-800 mb-6">Available Offers</h2>
            
            {offers.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Sparkles className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No offers available right now</p>
                  <p className="text-sm text-slate-400 mt-1">Check back later for new offers!</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-3 gap-6">
                {offers.map(offer => (
                  <Card key={offer.id} className={`hover:shadow-lg transition ${offer.claimed ? 'opacity-60' : ''}`} data-testid={`offer-${offer.id}`}>
                    <CardHeader>
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center mb-3">
                        <Sparkles className="w-6 h-6 text-white" />
                      </div>
                      <CardTitle className="text-lg">{offer.title}</CardTitle>
                      <CardDescription>{offer.description}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="text-center py-4">
                        <p className="text-3xl font-bold text-amber-500">+{offer.reward_amount}</p>
                        <p className="text-sm text-slate-500">AI</p>
                      </div>
                      {offer.min_balance && (
                        <p className="text-xs text-slate-500 text-center">
                          Requires minimum {offer.min_balance} {offer.min_balance === 1 ? "AI" : "AIs"} balance
                        </p>
                      )}
                    </CardContent>
                    <CardFooter>
                      {offer.claimed ? (
                        <Button disabled className="w-full" variant="outline">
                          <Check className="w-4 h-4 mr-2" />
                          Claimed
                        </Button>
                      ) : (
                        <Button
                          onClick={() => handleClaimOffer(offer.id)}
                          className="w-full bg-amber-500 hover:bg-amber-600"
                          data-testid={`claim-offer-${offer.id}`}
                        >
                          Claim Offer
                        </Button>
                      )}
                    </CardFooter>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Purchase Credits Tab */}
          <TabsContent value="purchase">
            <h2 className="text-xl font-semibold text-slate-800 mb-6">Purchase Credits</h2>
            
            <div className="grid grid-cols-3 gap-6">
              {creditPackages.map((pkg, i) => (
                <Card
                  key={pkg.id}
                  className={`hover:shadow-lg transition cursor-pointer ${i === 2 ? 'border-2 border-amber-500 relative' : ''}`}
                  data-testid={`package-${pkg.id}`}
                >
                  {i === 2 && (
                    <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-amber-500">
                      <Crown className="w-3 h-3 mr-1" />
                      Popular
                    </Badge>
                  )}
                  <CardHeader className="text-center">
                    <div className={`w-16 h-16 rounded-full mx-auto flex items-center justify-center ${i === 2 ? 'bg-amber-100' : 'bg-slate-100'}`}>
                      <Coins className={`w-8 h-8 ${i === 2 ? 'text-amber-500' : 'text-slate-500'}`} />
                    </div>
                    <CardTitle className="text-2xl mt-4">{pkg.amount} {pkg.amount === 1 ? "AI" : "AIs"}</CardTitle>
                    {pkg.bonus > 0 && (
                      <Badge variant="secondary" className="mt-2">
                        <Zap className="w-3 h-3 mr-1" />
                        +{pkg.bonus} Bonus
                      </Badge>
                    )}
                  </CardHeader>
                  <CardContent className="text-center">
                    {pkg.local_price ? (
                      <>
                        <p className="text-3xl font-bold text-slate-800">Package selected</p>
                      </>
                    ) : (
                      <>
                        <p className="text-3xl font-bold text-slate-800">Package selected</p>
                      </>
                    )}
                  </CardContent>
                  <CardFooter>
                    <Button
                      onClick={() => handlePurchaseCredits(pkg.id)}
                      className={`w-full ${i === 2 ? 'bg-amber-500 hover:bg-amber-600' : ''}`}
                      variant={i === 2 ? "default" : "outline"}
                      data-testid={`buy-package-${pkg.id}`}
                    >
                      Purchase
                    </Button>
                  </CardFooter>
                </Card>
              ))}
            </div>

            <Card className="mt-8">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center">
                    <Target className="w-6 h-6 text-emerald-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-slate-800">Custom Amount</h3>
                    <p className="text-sm text-slate-500">Need a specific amount? Purchase any amount of AI</p>
                  </div>
                  <Button variant="outline" onClick={() => window.location.href = '/aipps#packages'}>
                    Go to AI Page
                    <ChevronRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* History Tab */}
          <TabsContent value="history">
            <h2 className="text-xl font-semibold text-slate-800 mb-6">Transaction History</h2>
            
            <Card>
              <CardContent className="p-0">
                <ScrollArea className="h-[500px]">
                  {transactionHistory.length === 0 ? (
                    <div className="text-center py-12">
                      <Clock className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                      <p className="text-slate-500">No transactions yet</p>
                    </div>
                  ) : (
                    <div className="divide-y">
                      {transactionHistory.map((tx, i) => (
                        <div key={i} className="flex items-center justify-between p-4 hover:bg-app">
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${tx.amount > 0 ? 'bg-emerald-100' : 'bg-red-100'}`}>
                              {tx.amount > 0 ? (
                                <Plus className="w-5 h-5 text-emerald-600" />
                              ) : (
                                <Minus className="w-5 h-5 text-red-600" />
                              )}
                            </div>
                            <div>
                              <p className="font-medium text-slate-800">{tx.description}</p>
                              <p className="text-sm text-slate-500">
                                {format(new Date(tx.created_at), "MMM d, yyyy 'at' HH:mm")}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className={`font-bold ${tx.amount > 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                              {tx.amount > 0 ? '+' : ''}{tx.amount} {tx.amount === 1 ? "AI" : "AIs"}
                            </p>
                            <p className="text-xs text-slate-500">Balance: {tx.balance_after}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Create Competition Dialog */}
      <Dialog open={showCreateCompetition} onOpenChange={setShowCreateCompetition}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create Competition</DialogTitle>
            <DialogDescription>
              Set up a competition for users to participate in
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Title *</Label>
              <Input
                value={compForm.title}
                onChange={(e) => setCompForm({ ...compForm, title: e.target.value })}
                placeholder="Competition title"
                data-testid="comp-title-input"
              />
            </div>

            <div>
              <Label>Description *</Label>
              <Textarea
                value={compForm.description}
                onChange={(e) => setCompForm({ ...compForm, description: e.target.value })}
                placeholder="Describe the competition..."
                rows={3}
                data-testid="comp-description-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Entry Cost (AI)</Label>
                <Input
                  type="number"
                  min="0"
                  value={compForm.entry_cost}
                  onChange={(e) => setCompForm({ ...compForm, entry_cost: parseInt(e.target.value) || 0 })}
                  data-testid="comp-entry-cost-input"
                />
              </div>
              <div>
                <Label>Prize Pool (AI)</Label>
                <Input
                  type="number"
                  min="0"
                  value={compForm.prize_pool}
                  onChange={(e) => setCompForm({ ...compForm, prize_pool: parseInt(e.target.value) || 0 })}
                  data-testid="comp-prize-pool-input"
                />
              </div>
            </div>

            <div>
              <Label>Prize Description *</Label>
              <Input
                value={compForm.prize_description}
                onChange={(e) => setCompForm({ ...compForm, prize_description: e.target.value })}
                placeholder="What does the winner get?"
                data-testid="comp-prize-desc-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Max Participants</Label>
                <Input
                  type="number"
                  min="2"
                  value={compForm.max_participants || ""}
                  onChange={(e) => setCompForm({ ...compForm, max_participants: parseInt(e.target.value) || null })}
                  placeholder="Unlimited"
                />
              </div>
              <div>
                <Label>End Date *</Label>
                <Input
                  type="datetime-local"
                  value={compForm.ends_at}
                  onChange={(e) => setCompForm({ ...compForm, ends_at: e.target.value })}
                  data-testid="comp-end-date-input"
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowCreateCompetition(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreateCompetition} data-testid="submit-competition-btn">
                Create Competition
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* RewardsPal AI Chat */}
      {showAIPal && (
        <div className="fixed bottom-4 right-4 w-96 z-50">
          <AIPalChat
            palType="rewardspal"
            palName="RewardsPal"
            onClose={() => setShowAIPal(false)}
          />
        </div>
      )}
    </div>
  );
}

// Helper components
function Plus({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  );
}

function Minus({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  );
}

function Label({ children }) {
  return <label className="block text-sm font-medium text-slate-700 mb-1.5">{children}</label>;
}
